// src/components/Cart.tsx - Separate Cart Component
import React from 'react';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { toggleCart, removeFromCart, updateQuantity } from '../redux/slices/cartSlice';
import { FaTrash, FaPlus, FaMinus, FaTimes, FaShoppingBag, FaPepperHot } from 'react-icons/fa';
import { 
  getCartItemName, 
  getCartItemImage, 
  getCartItemSpiceLevel,
  getSpiceIconCount,
  formatModifiersForDisplayWithQuantity 
} from '../utils/cartUtils';
import { formatPrice } from '../utils/priceUtils';

const Cart: React.FC = () => {
  const appNavigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { items: cartItems, totalItems, totalAmount, isOpen } = useAppSelector(state => state.cart);

  const handleCartToggle = () => {
    dispatch(toggleCart());
  };

  const handleRemoveItem = (cartItemId: string) => {
    dispatch(removeFromCart(cartItemId));
  };

  const handleUpdateQuantity = (cartItemId: string, quantity: number) => {
    dispatch(updateQuantity({ cartItemId, quantity }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Transparent Backdrop */}
      <div 
        className="absolute inset-0 transition-opacity"
        onClick={handleCartToggle}
      />
      
      {/* Cart Sidebar */}
      <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-xl transform transition-transform">
        <div className="flex flex-col h-full">
          {/* Cart Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <FaShoppingBag className="w-6 h-6" style={{ color: '#FA2C37' }} />
              <h2 className="text-lg font-bold text-gray-900">Your Cart</h2>
              {totalItems > 0 && (
                <span className="text-white text-sm px-2 py-1 rounded-full" style={{ backgroundColor: '#FA2C37' }}>
                  {totalItems}
                </span>
              )}
            </div>
            <button
              onClick={handleCartToggle}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaTimes className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Cart Content */}
          <div className="flex-1 overflow-y-auto">
            {cartItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <FaShoppingBag className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-base font-semibold text-gray-900 mb-2">Your cart is empty</h3>
                <p className="text-sm text-gray-500 mb-6">Add some delicious items to get started!</p>
                <button
                  onClick={() => {
                    handleCartToggle();
                    appNavigate('/menu');
                  }}
                  className="text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  style={{ backgroundColor: '#FA2C37' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E02429'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FA2C37'}
                >
                  Browse Menu
                </button>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {cartItems.map((item) => (
                  <div key={item.cartItemId} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="p-4">
                      <div className="flex items-start">
                        {/* Item Image */}
                        <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 shadow-md">
                          {getCartItemImage(item) ? (
                            <img
                              src={getCartItemImage(item)}
                              alt={getCartItemName(item)}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.currentTarget;
                                target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(to bottom right, #FA2C37, #FA2C37)` }}>
                              <span className="text-2xl font-bold text-white">
                                {getCartItemName(item).charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Item Details */}
                        <div className="flex-1 min-w-0 ml-4">
                          {/* Top Row: Product Name and Total Price */}
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-gray-900 text-sm truncate">{getCartItemName(item)}</h4>
                            <span className="text-sm font-bold" style={{ color: '#FA2C37' }}>
                              {formatPrice(item.totalPrice * item.quantity)}
                            </span>
                          </div>
                          
                          {/* Second Row: Modifiers Display */}
                          {formatModifiersForDisplayWithQuantity(item.selected.modifiers, item.quantity).length > 0 && (
                            <div className="mb-2">
                              <div className="text-xs text-gray-600 space-y-1">
                                {formatModifiersForDisplayWithQuantity(item.selected.modifiers, item.quantity).map((modifier: any, index: number) => (
                                  <div key={index} className="flex items-center justify-between">
                                    <span className="font-medium">
                                      {modifier.options}
                                    </span>
                                    {modifier.price > 0 && (
                                      <span className="text-green-600">
                                        ({modifier.formattedPrice})
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Third Row: Spice Level (after modifiers) */}
                          {getCartItemSpiceLevel(item) && (
                            <div className="flex items-center gap-1 mb-3">
                              <div className="flex">
                                {Array.from({ length: getSpiceIconCount(getCartItemSpiceLevel(item)) }, (_, i) => (
                                  <FaPepperHot 
                                    key={i}
                                    className={`w-3 h-3 text-red-500 ${i > 0 ? 'ml-0.5' : ''}`} 
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Bottom Row: Quantity Controls and Delete */}
                          <div className="flex items-center justify-end">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                                <button
                                  onClick={() => handleUpdateQuantity(item.cartItemId, item.quantity - 1)}
                                  className="w-6 h-6 rounded-md bg-white flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEF2F2'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                  <FaMinus 
                                    className="w-2 h-2 text-gray-600 transition-colors"
                                    onMouseEnter={(e) => e.currentTarget.style.color = '#FA2C37'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
                                  />
                                </button>
                                <span className="w-7 text-center font-bold text-gray-900 text-sm">{item.quantity}</span>
                                <button
                                  onClick={() => handleUpdateQuantity(item.cartItemId, item.quantity + 1)}
                                  className="w-6 h-6 rounded-md bg-white flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEF2F2'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                  <FaPlus 
                                    className="w-2 h-2 text-gray-600 transition-colors"
                                    onMouseEnter={(e) => e.currentTarget.style.color = '#FA2C37'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
                                  />
                                </button>
                              </div>
                              
                              <button
                                onClick={() => handleRemoveItem(item.cartItemId)}
                                className="w-7 h-7 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center transition-all duration-200 group"
                              >
                                <FaTrash className="w-3 h-3 text-red-500 group-hover:text-red-600" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart Footer */}
          {cartItems.length > 0 && (
            <div className="border-t border-gray-200 p-4 space-y-4">
              {/* Total */}
              <div className="flex items-center justify-between text-lg font-bold">
                <span className="text-gray-900">Total</span>
                <span style={{ color: '#FA2C37' }}>{formatPrice(totalAmount)}</span>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    handleCartToggle();
                    appNavigate('/cart');
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  View Cart
                </button>
                <button
                  onClick={() => {
                    handleCartToggle();
                    appNavigate('/checkout');
                  }}
                  className="w-full text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  style={{ backgroundColor: '#FA2C37' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E02429'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FA2C37'}
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;
